import { Warehouse, Archive, AlertTriangle, Grid3X3, Box, Filter, PanelLeftClose, PanelLeft, PanelRightClose, PanelRight } from 'lucide-react';
import { WorkspaceProvider, useWorkspace, type TabType } from './lib/workspace';
import { FilterProvider, useFilter } from './lib/filter-context';
import { TabBar } from './components/workspace/TabBar';
import { FilterPanel } from './components/workspace/FilterPanel';
import { ProductPanel } from './components/workspace/ProductPanel';
import { MainTabContent, BottomTabContent } from './components/workspace/TabContent';

const sidebarModules: { type: TabType; icon: typeof Warehouse; label: string }[] = [
  { type: 'layout-overview', icon: Warehouse, label: 'Layout' },
  { type: 'missing', icon: AlertTriangle, label: 'Eksikler' },
  { type: 'assignments', icon: Grid3X3, label: 'Atamalar' },
  { type: 'special-bins', icon: Box, label: 'Ozel Binler' },
  { type: 'archive', icon: Archive, label: 'Arsiv' },
];

function Sidebar() {
  const { openTab, state } = useWorkspace();
  const activeMainType = state.main.tabs.find(t => t.id === state.main.activeTabId)?.type;

  function handleModuleClick(type: TabType, label: string) {
    const id = `module-${type}`;
    openTab('main', { id, type, label, params: {}, closable: type !== 'layout-overview' });
  }

  return (
    <aside className="fixed left-0 top-[var(--header-h)] bottom-0 w-[var(--sidebar-w)] bg-white border-r z-40 flex flex-col items-center py-2 gap-1"
      style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
      {sidebarModules.map(({ type, icon: Icon, label }) => {
        const isActive = activeMainType === type;
        return (
          <button key={type} title={label} onClick={() => handleModuleClick(type, label)}
            className={`relative group flex items-center justify-center w-10 h-10 rounded-sm transition-colors ${
              isActive ? 'bg-accent text-white' : 'text-tertiary hover:bg-hover hover:text-primary'
            }`}>
            <Icon size={18} strokeWidth={1.8} />
            <span className="absolute left-full ml-2 px-2 py-1 bg-primary text-white text-[10px] rounded-sm whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
              {label}
            </span>
          </button>
        );
      })}
    </aside>
  );
}

function Header() {
  const { state } = useWorkspace();
  const { isFilterActive, filteredItemIds, setPanelOpen, panelOpen } = useFilter();
  const activeTab = state.main.tabs.find(t => t.id === state.main.activeTabId);
  return (
    <header className="fixed top-0 left-0 right-0 h-[var(--header-h)] bg-white border-b z-50 flex items-center px-4 gap-4"
      style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
      <div className="flex items-center gap-2">
        <Warehouse size={20} className="text-accent" />
        <span className="text-[15px] font-semibold text-primary">ESL Depo</span>
      </div>
      <div className="w-px h-5" style={{ backgroundColor: 'rgba(33,35,39,0.1)' }} />
      <span className="text-[12px] text-secondary">{activeTab?.label || ''}</span>
      <div className="flex-1" />
      {isFilterActive && (
        <button onClick={() => setPanelOpen(!panelOpen)}
          className="flex items-center gap-1.5 px-2 py-1 bg-accent/10 text-accent rounded-sm text-[11px] font-medium hover:bg-accent/20">
          <Filter size={12} />
          {filteredItemIds.size} urun filtreli
        </button>
      )}
    </header>
  );
}

function MainPanel() {
  const { state, setActiveTab, closeTab } = useWorkspace();
  const { tabs, activeTabId } = state.main;
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {tabs.length > 1 && (
        <TabBar tabs={tabs} activeTabId={activeTabId}
          onActivate={id => setActiveTab('main', id)}
          onClose={id => closeTab('main', id)} />
      )}
      <div className="flex-1 flex flex-col min-h-0">
        {tabs.map(tab => (
          <div key={tab.id}
            className="flex-1 flex flex-col min-h-0"
            style={{ display: tab.id === activeTabId ? 'flex' : 'none' }}>
            <MainTabContent tab={tab} />
          </div>
        ))}
      </div>
    </div>
  );
}

function BottomPanel() {
  const { bottomPanel, setActiveTab, closeTab } = useWorkspace();
  const { tabs, activeTabId } = bottomPanel;
  if (tabs.length === 0) return null;

  return (
    <div className="border-t bg-white panel-transition flex flex-col" style={{ height: '38vh', minHeight: 200, borderColor: 'rgba(33,35,39,0.12)' }}>
      <TabBar tabs={tabs} activeTabId={activeTabId}
        onActivate={id => setActiveTab('bottom', id)}
        onClose={id => closeTab('bottom', id)} />
      <div className="flex-1 min-h-0 flex flex-col">
        {tabs.map(tab => (
          <div key={tab.id}
            className="flex-1 flex flex-col min-h-0"
            style={{ display: tab.id === activeTabId ? 'flex' : 'none' }}>
            <BottomTabContent tab={tab} />
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterToggle() {
  const { panelOpen, setPanelOpen, isFilterActive } = useFilter();
  return (
    <button
      onClick={() => setPanelOpen(!panelOpen)}
      className="flex h-full w-8 shrink-0 items-start justify-center border-r pt-3 transition-colors hover:bg-hover text-secondary hover:text-primary"
      style={{ borderColor: 'rgba(33,35,39,0.07)' }}
      title={panelOpen ? 'Filtreleri gizle' : 'Filtreleri goster'}>
      <div className="relative">
        {panelOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
        {isFilterActive && !panelOpen && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full" />
        )}
      </div>
    </button>
  );
}

function ProductToggle() {
  const { productPanelOpen, setProductPanelOpen, selectedItemId } = useFilter();
  return (
    <button
      onClick={() => setProductPanelOpen(!productPanelOpen)}
      className="flex h-full w-8 shrink-0 items-start justify-center border-l pt-3 transition-colors hover:bg-hover text-secondary hover:text-primary"
      style={{ borderColor: 'rgba(33,35,39,0.07)' }}
      title={productPanelOpen ? 'Urunleri gizle' : 'Urunleri goster'}>
      <div className="relative">
        {productPanelOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
        {selectedItemId && !productPanelOpen && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full" />
        )}
      </div>
    </button>
  );
}

function AppShell() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Sidebar />
      <main className="pl-[var(--sidebar-w)] pt-[var(--header-h)] h-screen flex flex-col">
        <div className="flex-1 flex min-h-0 min-w-0 overflow-hidden">
          <FilterPanel />
          <FilterToggle />
          <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
            <MainPanel />
            <BottomPanel />
          </div>
          <ProductToggle />
          <ProductPanel />
        </div>
      </main>
    </div>
  );
}

// Warm up Function App on first load
if (typeof window !== 'undefined') {
  fetch('https://esl-api.megafood.dk/api/health').catch(() => {});
}

export default function App() {
  return (
    <WorkspaceProvider>
      <FilterProvider>
        <AppShell />
      </FilterProvider>
    </WorkspaceProvider>
  );
}
